import ObjectCopyService from 'external-services/object-copy-service/index';
import NetworkConnectionService from 'external-services/network-connection-service/index';
import { AxiosInstance, RawAxiosResponseHeaders } from 'axios';
import {
  IThemeValue,
  ApiStatus,
  IAccount,
  NgLocalStorageKey,
  ILoginActions,
  IVenueActions,
  VenueActionNames,
  IDiaryVenue,
  IDiaryVenueMinimal,
  IPosIntegationDetails,
  IRole,
  AdminServiceLog,
  IPaymentSettings,
  LocalStorageKey,
} from 'shared-types/index';
import NetworkRequestsService from 'external-services/network-requests-service/index';
import { IAuthService } from 'external-services/diary-admin-service/types';
import LocalStorageService from 'external-services/local-storage-service/localStorage.service';
import { Roles } from 'external-services/manager-service/types';
import { IAllAccountsResponse } from './types';
import { cloneDeep } from 'lodash-es';

const NS = 'AdminService'; // jshint ignore:line

const ONE_MIN = 60000; // Milliseconds, 60,000ms = 1min
let TEN_MINS = 600000; // Milliseconds, 600,000ms = 10min
let CouchCheckFrequency = 900000; // Milliseconds, 900,000ms = 15min
const FOCUS_HANDLER_BUFFER = 300000; // Milliseconds, 300,000 = 5min

export const NumberOfAccounts = 20;

class AdminService {
  public reducedCouchUrl: string = null;

  private venue: IDiaryVenue = null;
  private account: IAccount = null;
  private isPosActive: boolean = null;
  private diaryStartCalledOneTime: boolean = false;
  private logConsoleDbEvents: boolean = true;

  private AuthService: IAuthService;
  private LoginActions: ILoginActions;
  private VenueActions: IVenueActions;

  private apiUrl: string;

  private posIntegrations: IPosIntegationDetails[];

  private previousFocusTime: number;
  private couchFreqTO: NodeJS.Timeout;

  private __testAllowedPeriodicChecks = 0;
  private periodicChecksCount = 0;
  private api: AxiosInstance;

  public init(
    AuthService: IAuthService,
    LoginActions: ILoginActions,
    VenueActions: IVenueActions,
    apiUrl: string,
    includeFocusHandler: boolean
  ) {
    this.AuthService = AuthService;
    this.LoginActions = LoginActions;
    this.VenueActions = VenueActions;
    this.api = NetworkRequestsService.getGlobalAPI();
    this.apiUrl = apiUrl;

    // When user get back on work, update the venue data
    includeFocusHandler && this.focus();
    this.LoginActions.handleLogout(() => this.logout());
    // periodically check couch name
    this.periodicCouchNameCheck();

    if (typeof jest !== 'undefined') {
      // don't need event handlers for unit tests
      return;
    }

    /**
     * Use this to test locally with shorter times
     * invalidateCouch can be false to keep couch url valid
     * window.dispatchEvent(new CustomEvent('debug-couch-frequency', {detail: {invalidateCouch: true}}));
     */
    window.addEventListener('debug-couch-frequency', (evt: any) => {
      CouchCheckFrequency = 3000;
      TEN_MINS = 6000;
      this.periodicCouchNameCheck();
      console.warn(
        NS,
        `'debug-couch-frequency' activated. Calling 'periodicCouchNameCheck' with much higher frequency`,
        evt
      );
      if (evt.detail.invalidateCouch) {
        this.reducedCouchUrl = 'https://fake-couch-url';
      }
    });
  }

  public setApiForUnitTest(api: AxiosInstance): void {
    this.api = api;
  }

  public setReducedCouchUrl(reducedCouchUrl: string): void {
    this.reducedCouchUrl = reducedCouchUrl;
  }

  /**
   * This function is for unit tests only
   */
  public __testClearData() {
    this.account = null;
    this.venue = null;
    this.diaryStartCalledOneTime = false;
  }

  /**
   * This function is for unit tests only
   */
  public __testSetVenue(venue: Partial<IDiaryVenue>) {
    this.venue = venue as IDiaryVenue;
  }

  public __testBlockPeriodicCheck(allowedChecks = 1): void {
    this.__testAllowedPeriodicChecks = allowedChecks;
  }

  public __testGetApi(): AxiosInstance {
    return this.api;
  }

  public resetBlockPeriodicCheck(): void {
    this.periodicChecksCount = 0;
  }

  // just created a function for this, so that unit tests can mock it
  public isDocumentHidden(): boolean {
    return document.hidden;
  }

  public getAccount(): IAccount {
    if (!this.account) {
      this.account = LocalStorageService.get(NgLocalStorageKey.account);
    }
    return this.account;
  }

  public setAccount(account: IAccount): void {
    this.account = account;
    LocalStorageService.set(NgLocalStorageKey.account, account);
  }

  public setAccountLocalStorageFromMemory(): void {
    if (this.account) {
      LocalStorageService.set(NgLocalStorageKey.account, this.account);
    }
  }

  public setVenueLocalStorageFromMemory(): void {
    if (this.venue) {
      LocalStorageService.set(NgLocalStorageKey.venue, this.venue);
    }
  }

  public getIPosStatusForVenue(): boolean {
    if (!this.isPosActive) {
      this.isPosActive = LocalStorageService.get(NgLocalStorageKey.isPosActive);
    }
    return this.isPosActive;
  }

  // to be only used to set the IPos status for the venue
  public setIPosStatusForVenue(isPosActive: boolean): void {
    this.isPosActive = isPosActive;
    LocalStorageService.set(NgLocalStorageKey.isPosActive, isPosActive);
  }

  public getVenue(): IDiaryVenue {
    if (!this.venue) {
      this.venue = LocalStorageService.get(NgLocalStorageKey.venue);
    }
    return this.venue;
  }

  public getVenueEndpointURL(): string {
    return this.apiUrl + '/restaurants/venues/' + this.getVenue().id + '/customers';
  }

  public getSharedTagsEndpointsURL(): string {
    return this.apiUrl + '/restaurants/accounts/' + this.getVenue().accountId + '/tags';
  }

  public getBookingOptionsEndpointsURL(): string {
    return this.apiUrl + '/bookings/venues/' + this.getVenue().id + '/booking-options';
  }

  /**
   * @todo: couldn't get AccountsController.cs version of this working. Should be using that as '/bookings/' is strictly meant for widget
   * could not use /restaurants/accounts - doesnt exist
   * could not use /accounts - non admin users dont have access
   */
  public getAccountEndpointsURL(accountId: string): string {
    return this.apiUrl + '/bookings/accounts/' + accountId;
  }

  public getSearchCustomerEndpointsURL(isAccountLevel: any): string {
    return (
      this.apiUrl +
      (isAccountLevel
        ? `/restaurants/search-customers/by-account/${this.getVenue().accountId}`
        : `/restaurants/search-customers/by-venue/${this.getVenue().id}`)
    );
  }

  public getMoreAccountInformation(accountId: string): string {
    return this.apiUrl + '/bookings/accounts/' + accountId;
  }

  public getVenueExcelReportURL(): string {
    return this.apiUrl + '/restaurants/venues/' + this.getVenue().id + '/excel-report';
  }

  public getThemeValues(_account?: IAccount, _venue?: { id: number }): IThemeValue {
    const account: IAccount = _account || this.getAccount();
    const currentVenue: { id: number } = _venue || this.getVenue();
    if (account.venues) {
      for (let i = account.venues.length - 1; i >= 0; i--) {
        const venue = this.getThemeValuesFromVenue(currentVenue.id, account.venues[i]);
        if (venue) {
          return venue;
        }
      }
    }
    return {
      theme: account.theme,
      accentColour: account.accentColour,
      font: account.widgetVersion === 2 ? account.font : null,
      widgetVersion: account.widgetVersion,
    };
  }

  public checkPin(pin: string): boolean {
    const allowedPins = this.getVenue().pins;
    let allowed = false;
    if (!allowedPins || !allowedPins.length) {
      // there are no pins set
      allowed = true;
    } else {
      for (let i = allowedPins.length - 1; i >= 0; i--) {
        const allowedPin = allowedPins[i];
        if (String(pin) === String(allowedPin)) {
          allowed = true;
          break;
        }
      }
    }
    return allowed;
  }

  public checkLogin(): boolean {
    // make sure the user is authenticated
    const isAuthenticated = this.AuthService.checkAuthSync();
    if (isAuthenticated) {
      // make sure the user has selected a venue
      const venue = this.getVenue();
      if (venue && venue.active) {
        return true;
      }
    }
    return false;
  }

  public getAccountById(accountId: string): Promise<IAccount> {
    return new Promise((resolve, reject) => {
      this.api
        .get(
          this.getAccountEndpointsURL(accountId)
        )
        .then((result) => {
          return resolve(result.data);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            // expired token
            this.AuthService.roleCheckLogout();
          } else {
            if (err?.response?.status === 503) {
              this.AuthService.showServerBusyMessage();
            }
            NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
          }
          reject(err);
        });
    });
  }

  public loadAccount(accountId: string = null): Promise<IAccount> {
    return new Promise((resolve, reject) => {
      this.api
        .get(
          this.apiUrl + '/me'
        )
        .then((result) => {
          return resolve(result.data);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            // expired token
            this.AuthService.roleCheckLogout();
          } else {
            if (err?.response?.status === 503) {
              this.AuthService.showServerBusyMessage();
            }
            NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
          }
          reject(err);
        });
    });
  }

  getVenuesInfo(accountData: IAccount, accountId: string, forceRefresh = false): Promise<IAccount> {
    return new Promise((resolve, reject) => {
      return this.getVenues(accountData, accountId, forceRefresh)
        .then((accountData: IAccount) => {
          this.setAccount(accountData);
          resolve(accountData);
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            // expired token
            this.AuthService.roleCheckLogout();
          } else {
            if (err?.response?.status === 503) {
              this.AuthService.showServerBusyMessage();
            }
            NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
          }
          reject(err);
        });
    });
  }

  public getAllAccounts(start: number, query?: string): Promise<IAllAccountsResponse> {
    return new Promise((resolve, reject) => {
      const params: {
        sort: string;
        count: number;
        start: number;
        q?: string;
      } = {
        count: NumberOfAccounts, // default get 20 records
        sort: 'businessName',
        start: start,
      };
      if (query) {
        params.q = query;
      }
      this.api
        .get(
          this.apiUrl + '/accounts',{ params: params }
        )
        .then((response: { data: IAccount[]; headers: RawAxiosResponseHeaders }) => {
          const contentRange = response.headers['content-range'];
          const quantity =
            contentRange != null && contentRange.indexOf('/') >= 0
              ? contentRange.split('/')[1]
              : '0';
          resolve({ data: response.data, quantity: parseInt(quantity, null) });
        })
        .catch((err) => {
          if (err?.response?.status === 401) {
            // expired token
            this.AuthService.roleCheckLogout();
          } else {
            if (err?.response?.status === 503) {
              this.AuthService.showServerBusyMessage();
            }
            NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
          }
          reject(err);
        });
    });
  }

  public checkApiState(): Promise<void> {
    if (this.AuthService) {
      return this.api
        .get(this.apiUrl + '/me')
        .then(() => {
          NetworkConnectionService.setApiStatus(ApiStatus.isOnline);
          return;
        })
        .catch(() => {
          return;
        });
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Logging to try and figure out why accountId is missing from getVenues call, which causes user to be
   * logged out.
   */
  private logGetVenuesTrace(accountData: IAccount): void {
    try {
      const traceError = new Error('Trace to getVenues');
      console.log(
        NS,
        'getVenues called',
        accountData
      );
      if (traceError.stack) {
        const stackWithoutMessage = traceError.stack
          .split('\n')
          .slice(1)
          .join('\n');
        console.log(NS, 'Upstream call stack prior to getVenues:\n' + stackWithoutMessage);

        this.serverLoggingDbEvents(
          "(No Venue Id)",
          `"accountId" missing from getVenues call`,
          AdminServiceLog.WARNLOG,
          `Stack trace: ${stackWithoutMessage}`
        );
      }
    } catch {
      // ignore logging failures
    }
  }

  public getVenues(accountData: IAccount, accountId: string, forceRefresh = false): Promise<IAccount> {
    return new Promise((resolve, reject) => {

      if (!accountId) {
        this.logGetVenuesTrace(accountData);
      }

      if (this.getAccount() && !forceRefresh) {
        resolve(this.getAccount());
      } else {
        const params: {
          accountId?: string;
          count: number;
        } = {
          count: 1000,
        };
        if (accountId) {
          params.accountId = accountId;
        }
        
        this.api
          .get(
            this.apiUrl + '/restaurants/venues', { params: params }
          )
          .then((response: { data: IDiaryVenueMinimal[] }) => {
            accountData.venues = response.data;
            // this.setAccount(accountData); // Already setting in getVenuesInfo
            resolve(accountData);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  }

  /**
   * If the venue CouchDB names don't match up, a reload is required.
   * We check this on every loadVenue request (eg new/edit/drag/quick-pax/quick-status booking change.
   * We also do a recurring periodic check via cheapCouchHealthCheck, which puts less strain on API
   * than calling loadVenue.
   */
  private venueCouchNameCheckWithReloadPrompt(reducedCouchUrl: string, venueId: number) {
    if (
      venueId &&
      this.reducedCouchUrl &&
      reducedCouchUrl &&
      reducedCouchUrl !== this.reducedCouchUrl
    ) {
      this.VenueActions.venueAction(VenueActionNames.RELOAD_NEEDED, { venueId });
      this.serverLoggingDbEvents(
        venueId.toString(),
        `Couch Reduced URL change has forced reload.`,
        AdminServiceLog.WARNLOG,
        `Old reducedCouchUrl: ${this.reducedCouchUrl}. New reducedCouchUrl: ${reducedCouchUrl}`
      );
    }
  }

  /**
   * Request for the couchDB url on a given venue, used to verify the correct couchDB url is being used.
   * Sometimes this url will change, when CouchDB gets compacted, for example, so this must be periodically
   * checked against the venue.
   */
  private async cheapCouchHealthCheck() {
    const accountId = this.venue?.accountId;
    const venueId = this.venue?.id;

    if (!accountId || !venueId) {
      console.warn(
        NS,
        `Couldn't call 'cheapCouchHealthCheck'
      because accountId (${accountId}) and venueId (${venueId}) must be populated.`
      );
      return;
    }

    try {
      const response: {
        data: { couchDatabase: string; couchDatabaseReduced: string };
      } = await this.api.get(this.apiUrl + '/restaurants/couch-check', {
        params: { accountId, venueId },
      });
      console.log(
        NS,
        'couch-check couchDatabase/couchDatabaseReduced',
        response.data.couchDatabaseReduced
      );
      this.venueCouchNameCheckWithReloadPrompt(response.data.couchDatabaseReduced, venueId);
    } catch (err) {
      console.warn(NS, 'cheapCouchHealthCheck failed', err);
    }
  }

  public async loadVenue(
    id: string,
    dontBroadcast?: boolean,
    checkReduced?: boolean,
    needFetchMorePaymentSettings = false
  ): Promise<IDiaryVenue> {
    // this.oneTimeEntryLog(id);
    
    try {
      const response: { data: IDiaryVenue } = await this.api.get(
        this.apiUrl + '/restaurants/venues/' + id, {
              params: {
                ignoreUser: true,
              },
            }

      );

      /**
       * !!!!!!!!
       * @todo: do we need a new IVenue just for diary venue object? for reducedCouchUrl & posIntegrations & pins
       * Or is this also available for widget?
       * Severity: high
       * !!!!!!!!
       */

      const venue: IDiaryVenue = response.data;

      if (needFetchMorePaymentSettings) {
        const paymentSettings = await this.fetchMorePaymentSettings(venue);

        if (paymentSettings) {
          venue.paymentSettings = paymentSettings;
        }
      }

      if (checkReduced) {
        this.venueCouchNameCheckWithReloadPrompt(venue.reducedCouchUrl, venue.id);
      }

      /**
       * BREAKING CHANGE FOR DIARY MOBILE: Will need to call loadIPOS externally before loadVenue
       */
      // venue.posIntegrations = await this.loadPosSubscribedPartners(venue.id);
      venue.posIntegrations = this.getIPOSIntegrations();
      this.setVenue(venue);

      NetworkConnectionService.setApiStatus(ApiStatus.isOnline);
      if (!dontBroadcast) {
        this.VenueActions.venueAction(VenueActionNames.VENUE_LOADED);
      }

      return venue;
    } catch (error) {
      const err = error as any;
      // let shouldGoOffline = true;

      // these statuses do not mean you are offline! @todo: may need to reimplement this at some point
      // switch (err?.response?.status) {
      //   case 400: // bad request
      //   case 403: // forbidden
      //   case 404: // not found
      //   case 405: // method not allowed
      //   case 409: // conflict
      //     shouldGoOffline = false;
      //     break;
      // }
      // NetworkConnectionService.setApiStatus(shouldGoOffline ? ApiStatus.isOffline : ApiStatus.isOnline);

      if (err?.response?.status === 401) {
        // expired token
        this.AuthService.roleCheckLogout(id);
      } else {
        if (err?.response?.status === 503) {
          this.AuthService.showServerBusyMessage();
        }
        NetworkConnectionService.setApiStatus(ApiStatus.isOffline);
      }

      throw err; // rethrow so chained functions can still catch it
    }
  }

  public async oneTimeEntryLog(venueId: string): Promise<void> {
    if (!this.diaryStartCalledOneTime) {
      await this.api
        .get(this.apiUrl + '/restaurants/diary-start/' + venueId)
        .then(() => {
          // Do nothing with response :)
          this.diaryStartCalledOneTime = true;
        })
        .catch(() => {
          // Do nothing if it errors - we're just here to log diary start
        });
    }
  }

  public isPartnerBookingsLocked(posVendorName?: string): boolean {
    let markLocationBookingLocked = false;

    if (posVendorName !== null && posVendorName !== 'undefined') {
      const currentVenue = this.getVenue();
      markLocationBookingLocked =
        currentVenue?.posIntegrations?.find(
          (integration) => integration.posVendorName === posVendorName
        )?.markLocationBookingsLocked ?? false;
    }

    return markLocationBookingLocked;
  }

  public async fetchMorePaymentSettings(venue: IDiaryVenue): Promise<IPaymentSettings> {
    // Get PaymentInfor
    const accountResponse: { data: IAccount } = await this.api.get(
      this.getMoreAccountInformation(venue.accountId),
      {
        params: {
          ignoreUser: true,
        },
      }
    );
    const paymentSettings = accountResponse?.data?.ownedVenues?.find(
      (v) => v.id === venue.id
    )?.paymentSettings;

    return paymentSettings;
  }

  public getVenueTimezoneId(): string {
    return this.getVenue().timeZoneId;
  }

  public serverLoggingDbEvents(
    venueId: string,
    message: string,
    logType: string,
    logNotes: string = ''
  ): void {
    if (this.logConsoleDbEvents) console.log(`[Admin] ==> Sending SEQ ${logType} logging`);

    try {
      this.api.get(
        this.apiUrl + '/restaurants/diary-log/' + venueId, {
              params: {
                message: message + ' (v0.3)',
                type: logType,
                notes: logNotes,
              },
            }
      );
      // console logging
      if (this.logConsoleDbEvents) console.log('[Admin] ==> Seq Logging Created');
    } catch {
      // Do nothing if it errors - we're just here to log diary start
      if (this.logConsoleDbEvents) console.warn('[Admin] ==> ERROR when SEQ logging');
    }
  }

  private async loadPosSubscribedPartners(venueId: number): Promise<IPosIntegationDetails[]> {
    return this.api
      .get(
        this.apiUrl + '/restaurants/venues/' + venueId + '/subscribed-pos-partners'
      )
      .then((result) => {
        return result.data;
      });
  }

  // used in the diary - works differently cause venue information is loaded before we call ipos data
  async loadIPOS(venueId: number) {
    const posIntegrations = await this.loadPosSubscribedPartners(venueId);
    this.setIPOSIntegrations(posIntegrations);
    let venue = cloneDeep(this.getVenue());
    venue = { ...venue, posIntegrations };
    this.setVenue(venue);
  }

  // Diary works differently than DM cause in Diary Load Venue gets called multiple times and its already loaded before we call loadIPos
  // Something to do with Object Mutation - if we have to save it we should use the venueAction to save the data
  async loadIPOSDM(venueId: number) {
    const posIntegrations = await this.loadPosSubscribedPartners(venueId);
    this.setIPOSIntegrations(posIntegrations);
  }

  setIPOSIntegrations(posIntegrations: IPosIntegationDetails[]) {
    this.posIntegrations = posIntegrations;
  }

  getIPOSIntegrations() {
    return this.posIntegrations;
  }

  // *** Private function *** //

  private setVenue(venue: IDiaryVenue): void {
    const v: IDiaryVenue = ObjectCopyService.angularSafeCopy(venue);
    this.venue = v;
    LocalStorageService.set(NgLocalStorageKey.venue, v);
    NetworkConnectionService.setCouchurl(v.couchUrl);
  }

  removeVenue(): void {
    this.venue = null;
    LocalStorageService.remove(NgLocalStorageKey.venue);
  }

  public getThemeValuesFromVenue(currentVenueId: number, venue: IDiaryVenueMinimal): IThemeValue {
    if (currentVenueId !== venue.id) {
      return null;
    }
    if (venue.isUsingAccountWidgetSettings || !venue.theme || !venue.accentColour) {
      if (venue.isUsingAccountWidgetSettings == false) {
        // returns default theme config - NBI-2518 (when accentColour is blank causing errors)
        return {
          theme: venue.theme ? venue.theme : '',
          accentColour: venue.accentColour ? venue.accentColour : '',
          font: venue.font ? venue.font : '',
          widgetVersion: venue.widgetVersion,
        };
      } else {
        return null;
      }
    }
    return {
      theme: venue.theme,
      accentColour: venue.accentColour,
      font: venue.widgetVersion === 2 ? venue.font : null,
      widgetVersion: venue.widgetVersion,
    };
  }

  private focus(): void {
    const focusHandler = () => {
      const timeNow = Date.now();
      if (timeNow < this.previousFocusTime + FOCUS_HANDLER_BUFFER) {
        /**
         * Don't trigger any account or venue checks on focus, if they've already been checked in the last 5 minutes.
         * This gets called when switching tabs, or locking device and reopening.
         */
        const remainingTimeInBufferMS = this.previousFocusTime + FOCUS_HANDLER_BUFFER - timeNow;
        console.log(
          NS,
          `Skipping focus handler get venue and me controller check, as there is still ${remainingTimeInBufferMS} milliseconds left (about ${Math.round(
            remainingTimeInBufferMS / 60000
          )} minutes).`
        );
        return;
      }
      this.previousFocusTime = timeNow;

      if (this.getAccount()) {
        this.loadAccount()
          .then(() => {
            const venue = this.getVenue();
            if (venue && venue.id) {
              this.periodicCouchNameCheck(); // clearing the periodicCouchNameCheck, so the counter starts again
              this.loadVenue(venue.id.toString(), true, true);
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    };
    // window.dispatchEvent(new Event('disable-focus-auth-handler'));

    // When user get back on work, update the venue data
    window.addEventListener('focus', focusHandler);
    window.addEventListener('disable-focus-auth-handler', () => {
      window.removeEventListener('focus', focusHandler);
    });
  }

  private logout(): void {
    this.venue = null;
    this.account = null;
    LocalStorageService.remove(NgLocalStorageKey.account);
    LocalStorageService.remove(NgLocalStorageKey.venue);
    LocalStorageService.remove(NgLocalStorageKey.isManager);
    LocalStorageService.remove(NgLocalStorageKey.couchUsers);
    LocalStorageService.remove(LocalStorageKey.offlineStatus); // NBI-9452 offline status used in Diary for logging to Pendo
  }

  /**
   * Same as calling periodicCouchNameCheck, but made public and given more
   * descriptive method name.
   */
  public restartPeriodicCouchNameCheck() {
    this.periodicCouchNameCheck();
  }

  /**
   * Waits 15 minutes (plus a random extra 1-10 minutes) to call `loadVenue`. Calling multiple times will cancel
   * previous timer and start the counter again.
   * @todo: write unit tests to cover this
   */
  private periodicCouchNameCheck() {
    if (this.couchFreqTO) {
      // prevents periodicCouchNameCheck from accumulating
      console.log(NS, 'periodicCouchNameCheck', `Timer restarted.`);
      clearTimeout(this.couchFreqTO);
    }

    // up to 10 minutes extra time, spread out to minimise API spikes
    const randomExtraMins = Math.round((Math.random() * TEN_MINS) / 6000) / 10; // rounded to each minute with 1 decimal
    const DELAY_IN_MS = CouchCheckFrequency + randomExtraMins * ONE_MIN;
    const DELAY_IN_MINS = CouchCheckFrequency / ONE_MIN + randomExtraMins;
    console.log(
      NS,
      'periodicCouchNameCheck',
      `Will call again to check couch url again in about ${DELAY_IN_MINS} minutes (${DELAY_IN_MS}ms).`
    );
    this.couchFreqTO = setTimeout(async () => {
      const venue = this.getVenue();
      // if window is unfocused (eg screen locked or minimised), don't keep calling loadVenue
      if (this.isDocumentHidden()) {
        console.log(
          NS,
          'periodicCouchNameCheck',
          `cheapCouchHealthCheck skipped due to window being hidden or unfocused.`
        );
      } else if (venue && venue.id) {
        console.log(
          NS,
          'periodicCouchNameCheck',
          `Calling cheapCouchHealthCheck to check couch url.`
        );
        /**
         * Note: we used to call loadVenue here, but cheapCouchHealthCheck is lighter on API and all we use it for
         * is checking the couch url matches venue.reducedCouchUrl, so it is enough. Changes as part of NBI-6531.
         */
        await this.cheapCouchHealthCheck();
      }

      // just used for unit tests, to keep track of polling
      this.periodicChecksCount++;
      if (
        this.__testAllowedPeriodicChecks &&
        this.periodicChecksCount >= this.__testAllowedPeriodicChecks
      ) {
        console.log(NS, 'Prevented further periodic couch name checks.');
        return;
      }

      this.periodicCouchNameCheck();
    }, DELAY_IN_MS);
  }

  checkForAdminRoles(roles: IRole[]) {
    const adminRoles = [
      Roles.LoyaltySolutionAdmin,
      Roles.BookItAdmin,
      Roles.BookItStaff,
      Roles.BookItAccountExporter,
    ];
    return roles.some(({ name }) => {
      return adminRoles.includes(name as Roles);
    });
  }
}

const instance = new AdminService();
export default instance as AdminService;
